import { EmployeeI } from '../domain/Employee';
import { IEmployee } from '../domain/NewEmployee';
import EmployeesRepositoryI, {
  CreateEmployeeReqI,
  UpdateEmployeeReqI,
} from './EmployeesRepositoryI';
import { FetcherAmplify } from 'src/business/Libraries/Fetcher/FetcherAmplify';

export default class EmployeesRepositoryImpl implements EmployeesRepositoryI {
  private fetcherAmplify;

  constructor(fetcherAmplify: FetcherAmplify) {
    this.fetcherAmplify = fetcherAmplify;
  }

  private getExtensionFromMime(mimeType: string): string {
    const map: Record<string, string> = {
      'application/pdf': '.pdf',
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
    };
    return map[mimeType] ?? '';
  }

  private async uploadAttachment(
    customerId: string,
    employeeId: string,
    fileName: string,
    file: File,
  ): Promise<void> {
    const formData = new FormData();
    const nameWithExt = `${fileName}${this.getExtensionFromMime(file.type)}`;
    formData.append('file', new File([file], nameWithExt, { type: file.type }));
    await this.fetcherAmplify.post(
      `/v1/customers/${customerId}/employees/${employeeId}/attachments`,
      { body: formData },
    );
  }

  public async createEmployee({
    customerId,
    employee,
  }: {
    customerId: string;
    employee: CreateEmployeeReqI;
  }): Promise<void> {
    const response = await this.fetcherAmplify.post(
      `/v1/customers/${customerId}/employees`,
      {
        body: {
          ...employee,
        },
      },
    );

    return response;
  }

  public async getEmployees(customerId: string): Promise<
    (Omit<EmployeeI, 'roles'> & {
      roles: string[];
    })[]
  > {
    return this.fetcherAmplify.get(`/v1/customers/${customerId}/employees`);
  }

  public async getEmployee(
    customerId: string,
    employeeId: string,
  ): Promise<any> {
    return this.fetcherAmplify.get(
      `/v1/customers/${customerId}/employees/${employeeId}`,
    );
  }

  public async updateEmployee({
    customerId,
    employee,
    employeeId,
  }: {
    employeeId: string;
    customerId: string;
    employee: UpdateEmployeeReqI;
  }): Promise<void> {
    return this.fetcherAmplify.put(
      `/v1/customers/${customerId}/employees/${employeeId}`,
      {
        body: {
          ...employee,
        },
      },
    );
  }

  public async newGetEmployees({
    customerId,
  }: {
    customerId: string;
  }): Promise<Partial<IEmployee>[]> {
    return this.fetcherAmplify.get(`/v1/customers/${customerId}/employees`, {
      queryStringParameters: {
        fullData: true,
      },
    });
  }

  public async newCreateEmployee({
    customerId,
    employee,
    attachments,
  }: {
    customerId: string;
    employee: Partial<IEmployee>;
    attachments?: { documentoContacto?: File };
  }): Promise<any> {
    const response = await this.fetcherAmplify.post(
      `/v1/customers/${customerId}/employees`,
      {
        body: {
          ...employee,
        },
      },
    );

    if (attachments?.documentoContacto && response?.id) {
      await this.uploadAttachment(
        customerId,
        response.id,
        'documentoContacto',
        attachments.documentoContacto,
      );
    }

    return response;
  }

  public async newUpdateEmployee({
    customerId,
    employeeId,
    employee,
    attachments,
  }: {
    customerId: string;
    employeeId: string;
    employee: Partial<IEmployee>;
    attachments?: { documentoContacto?: File };
  }): Promise<any> {
    const response = await this.fetcherAmplify.put(
      `/v1/customers/${customerId}/employees/${employeeId}`,
      {
        body: {
          ...employee,
        },
      },
    );

    if (attachments?.documentoContacto) {
      await this.uploadAttachment(
        customerId,
        employeeId,
        'documentoContacto',
        attachments.documentoContacto,
      );
    }

    return response;
  }
}
